import "./tournament-message-tournament-select.scss";
import { useEffect, useMemo, useState } from "react";
import {
  GSEmptyList,
  GSForm,
  GSInfiniteList,
  GSSidePanelPage,
} from "golfstatus_react_components";
import {
  faChevronLeft,
  faSave,
} from "@fortawesome/free-solid-svg-icons";
import { useMatch, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getMessage,
  patchMessage,
  selectCurrentMessageTournaments,
  selectMessagesLoading,
  selectMessagesNotifications,
  selectSelectedMessageTournaments,
  clearNotifications,
} from "../../../reducers/messagesSlice";
import {
  searchTmsTournaments,
  setSearchedTournaments,
  selectCurrentTournament,
  selectSearchedTournaments,
  selectLoading as selectTournamentLoading,
} from "../../../reducers/tournamentSlice";
import { useNotificationBanner } from "../../../hooks/notificationHooks";
import { getNotificationItemInfo } from "../../../helpers/Converters";
import { tournamentSelectForm } from "../../../forms/MessageForms";

const TournamentMessageTournamentSelect = () => {
  const navigate = useNavigate();
  const match = useMatch("/:tournamentID/messages/:messageID/tournament-select");
  const dispatch = useDispatch();
  const loading = useSelector(selectMessagesLoading);
  const notifications = useSelector(selectMessagesNotifications);
  const currentTournament = useSelector(selectCurrentTournament);
  const searchedTournaments = useSelector(selectSearchedTournaments);
  const tournamentLoading = useSelector(selectTournamentLoading);
  const tournamentsLoading =
    tournamentLoading?.includes?.("searchTmsTournaments") ?? false;

  const ownerTournamentID = match?.params?.tournamentID;

  const additionalTournaments = useSelector(selectCurrentMessageTournaments);
  const selectedTournaments = useSelector((state) =>
    selectSelectedMessageTournaments(state, ownerTournamentID, currentTournament)
  );

  const [name, setName] = useState("");
  const [filter, setFilter] = useState({
    page: { number: 1, size: 15 },
    name: "",
  });

  const hasMoreTournaments =
    searchedTournaments.length >= filter.page.number * filter.page.size;

  useEffect(() => {
    dispatch(getMessage({ tournamentID: ownerTournamentID, messageID: match?.params?.messageID }));
  }, [dispatch, ownerTournamentID, match?.params?.messageID]);

  useEffect(() => {
    if (filter.page.number === 1) {
      dispatch(setSearchedTournaments([]));
    }
    dispatch(searchTmsTournaments(filter));
  }, [dispatch, filter]);

  const selectedIds = useMemo(
    () => new Set(selectedTournaments.map((t) => t?.id)),
    [selectedTournaments]
  );

  const availableTournaments = useMemo(
    () =>
      (searchedTournaments ?? []).filter(
        (t) => t?.id && !selectedIds.has(t.id)
      ),
    [searchedTournaments, selectedIds]
  );

  const persistAdditional = (nextAdditional) => {
    dispatch(
      patchMessage({
        tournamentID: ownerTournamentID,
        messageID: match?.params?.messageID,
        data: {
          tournaments: nextAdditional.map((t) => ({ id: t.id })),
        },
      })
    );
  };

  const addTournament = (tournament) => {
    if (!tournament?.id || selectedIds.has(tournament.id)) return;
    persistAdditional([...additionalTournaments, tournament]);
  };

  const removeTournament = (tournament) => {
    if (!tournament?.id || tournament.id === ownerTournamentID) return;
    persistAdditional(additionalTournaments.filter((t) => t?.id !== tournament.id));
  };

  const goBack = () => {
    dispatch(clearNotifications());
    navigate(-1);
  };

  const getNavigation = () => ({
    title: "Tournaments",
    leftIcon: faChevronLeft,
    leftButtonClick: goBack,
  });

  const submitSearch = () => {
    setFilter((prev) => ({
      ...prev,
      name,
      page: { ...prev.page, number: 1 },
    }));
  };

  const clearSearch = () => {
    setName("");
    setFilter((prev) => ({
      ...prev,
      name: "",
      page: { ...prev.page, number: 1 },
    }));
  };

  const loadMore = () => {
    if (tournamentsLoading || !hasMoreTournaments) return;
    setFilter((prev) => ({
      ...prev,
      page: { ...prev.page, number: prev.page.number + 1 },
    }));
  };

  const getEmptySearchMessage = () => (
    <GSEmptyList
      title="We couldn't find the tournament you're looking for..."
      detail="Try changing the search term."
      actions={[{ title: "Clear Search", onClick: clearSearch }]}
    />
  );

  const getEmptyListMessage = () => (
    <GSEmptyList
      title="No other tournaments available"
      detail="There are no other tournaments to add recipients from."
    />
  );

  const formContext = {
    selectedTournaments,
    availableTournaments,
    ownerTournamentID,
    name,
    setName,
    submitSearch,
    clearSearch,
    filter,
    addTournament,
    removeTournament,
    getEmptySearchMessage,
    getEmptyListMessage,
    tournamentsLoading,
  };

  const getDefaultBanner = () => ({
    title: getNotificationItemInfo("Selections will be automatically saved.", faSave),
    state: "grey",
  });

  const timeoutAction = () => {
    dispatch(clearNotifications());
  };

  const [banner] = useNotificationBanner({
    notifications,
    defaultBanner: getDefaultBanner(),
    timeoutAction,
  });

  return (
    <tournament-message-tournament-select>
      <GSSidePanelPage
        header={getNavigation()}
        banner={banner}
        content={
          <GSInfiniteList
            loadMore={loadMore}
            filter={{ ...filter, page: { ...filter.page } }}
          >
            <GSForm
              formTitle="Tournaments"
              formSections={tournamentSelectForm(formContext)}
            />
          </GSInfiniteList>
        }
        drawer={{
          actions: [
            {
              name: "Done",
              action: goBack,
              type: "light-grey",
              isDisabled: loading?.length > 0,
            },
          ],
        }}
      />
    </tournament-message-tournament-select>
  );
};

export default TournamentMessageTournamentSelect;
